import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  QrCode, Copy, Download, Power, Save, FileText, Users, CalendarDays,
  FolderOpen, Vote, Plus, Trash2, MapPin, BarChart3, ClipboardPaste, CopyPlus, ArrowRightLeft, GripVertical, ChevronUp, ChevronDown, Eye, ClipboardList,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { userRoles, SAKRAMENT_LABELS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import FormBuilder from "@/components/FormBuilder";
import FormRenderer from "@/components/FormRenderer";
import { RichTextEditor, RichContent } from "@/components/RichTextEditor";

export default function StaffCourse({ course, reload }) {
  const [tab, setTab] = useState("einstellungen");
  const [qr, setQr] = useState(null);
  const [beschreibung, setBeschreibung] = useState(course.beschreibung || "");
  const [fields, setFields] = useState(course.form_fields_gemeinsam || course.form_fields || []);
  const [dsTemplates, setDsTemplates] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplSak, setTplSak] = useState(course.sakrament);
  const [regs, setRegs] = useState([]);
  const [termine, setTermine] = useState([]);
  const [files, setFiles] = useState([]);
  const [polls, setPolls] = useState([]);
  const [stats, setStats] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [moveReg, setMoveReg] = useState(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [dupOpen, setDupOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFields, setPreviewFields] = useState([]);
  const [previewSkip, setPreviewSkip] = useState(false);
  const [dup, setDup] = useState({ kurs_name: "", jahr: course.jahr, monat: course.monat || "", ort: course.ort || "", kurs_gemeinde: course.kurs_gemeinde || "" });
  const { user } = useAuth();
  const canMove = userRoles(user).some((r) => r === "admin" || r === "user-verwaltung");

  const cid = course.id;
  const navigate = useNavigate();
  const [edit, setEdit] = useState({
    kurs_name: course.kurs_name, sakrament: course.sakrament, jahr: course.jahr, monat: course.monat || "",
    ort: course.ort || "", kurs_gemeinde: course.kurs_gemeinde || "",
    kurs_beginn: course.kurs_beginn || "", kurs_ende: course.kurs_ende || "",
    online_until: course.online_until || "",
  });

  useEffect(() => {
    api.get(`/courses/${cid}/qr`).then(({ data }) => setQr(data)).catch(() => {});
    api.get(`/courses`).then(({ data }) => setAllCourses(data)).catch(() => {});
    api.get(`/datenschutz-templates`).then(({ data }) => setDsTemplates(data)).catch(() => {});
    api.get(`/form-templates`).then(({ data }) => setFormTemplates(data)).catch(() => {});
    reloadAll();
    // eslint-disable-next-line
  }, [cid]);

  const reloadAll = () => {
    api.get(`/courses/${cid}/registrations`).then(({ data }) => setRegs(data)).catch(() => {});
    api.get(`/courses/${cid}/termine`).then(({ data }) => setTermine(data)).catch(() => {});
    api.get(`/courses/${cid}/files`).then(({ data }) => setFiles(data)).catch(() => {});
    api.get(`/courses/${cid}/polls`).then(({ data }) => setPolls(data)).catch(() => {});
    api.get(`/courses/${cid}/stats`).then(({ data }) => setStats(data)).catch(() => {});
  };

  const saveCourse = async () => {
    try {
      await api.put(`/courses/${cid}`, { ...edit, jahr: Number(edit.jahr), online_until: edit.online_until || null });
      toast.success("Kurs gespeichert."); reload();
      window.dispatchEvent(new Event("courses-changed"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteCourse = async () => {
    if (!window.confirm("Diesen Kurs wirklich löschen? Alle Anmeldungen und Daten gehen verloren.")) return;
    await api.delete(`/courses/${cid}`);
    toast.success("Kurs gelöscht.");
    window.dispatchEvent(new Event("courses-changed"));
    navigate("/kurse");
  };

  const toggleOnline = async () => {
    await api.put(`/courses/${cid}`, { online: !course.online });
    toast.success(course.online ? "Kurs ist jetzt offline." : "Kurs ist jetzt online.");
    reload();
    window.dispatchEvent(new Event("courses-changed"));
  };

  const saveBeschreibung = async () => {
    await api.put(`/courses/${cid}`, { beschreibung });
    toast.success("Kursbeschreibung gespeichert.");
  };

  const saveForm = async () => {
    await api.put(`/courses/${cid}`, { form_fields_gemeinsam: fields, form_fields: fields });
    toast.success("Anmeldeformular gespeichert.");
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return toast.error("Bitte einen Namen vergeben.");
    try {
      await api.post(`/form-templates`, { sakrament: tplSak, name: tplName, fields });
      toast.success("Als Vorlage gespeichert (Verwaltung › Anmeldeformulare).");
      setSaveTplOpen(false); setTplName("");
      api.get(`/form-templates`).then(({ data }) => setFormTemplates(data)).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const applyTemplate = (tid) => {
    const t = formTemplates.find((x) => x.id === tid);
    if (!t) return;
    setFields(t.fields || []);
    toast.success("Vorlage übernommen. Bitte noch speichern.");
  };

  const copyForm = () => {
    localStorage.setItem("kat_form_clipboard", JSON.stringify(fields));
    toast.success("Formular kopiert. Sie können es in einem anderen Kurs einfügen.");
  };
  const pasteForm = () => {
    const raw = localStorage.getItem("kat_form_clipboard");
    if (!raw) return toast.error("Kein kopiertes Formular vorhanden.");
    try {
      const f = JSON.parse(raw).map((x) => ({ ...x, id: Math.random().toString(36).slice(2, 10) }));
      setFields(f);
      toast.success("Formular eingefügt. Bitte noch speichern.");
    } catch { toast.error("Kopiertes Formular ist ungültig."); }
  };
  const importFromCourse = async (id) => {
    const { data } = await api.get(`/courses/${id}`);
    const f = (data.form_fields || []).map((x) => ({ ...x, id: Math.random().toString(36).slice(2, 10) }));
    if (f.length === 0) return toast.error("Der gewählte Kurs hat kein Formular.");
    setFields(f);
    toast.success("Formular übernommen. Bitte noch speichern.");
  };

  const copyLink = () => { navigator.clipboard.writeText(qr.link); toast.success("Link kopiert."); };

  const doMove = async () => {
    if (!moveTarget) return toast.error("Bitte Zielkurs wählen.");
    try {
      await api.post(`/registrations/${moveReg.id}/move`, { target_course_id: moveTarget });
      toast.success("Anmeldung verschoben.");
      setMoveReg(null); setMoveTarget(""); reloadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDuplicate = async () => {
    try {
      const { data } = await api.post(`/courses/${cid}/duplicate`, {
        kurs_name: dup.kurs_name || undefined,
        jahr: dup.jahr ? Number(dup.jahr) : undefined,
        monat: dup.monat, ort: dup.ort, kurs_gemeinde: dup.kurs_gemeinde,
      });
      toast.success("Kurs dupliziert. Bitte Angaben prüfen.");
      setDupOpen(false);
      window.dispatchEvent(new Event("courses-changed"));
      window.location.href = `/kurs/${data.id}`;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8">
      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-3xl font-serif">{course.kurs_name}</h1>
        <span className="text-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{course.group}</span>
        {course.expired && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">offline</span>}
      </div>
      <p className="text-muted-foreground text-sm mb-6 flex items-center gap-1"><MapPin size={14} /> {course.kurs_gemeinde}</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="einstellungen" data-testid="tab-einstellungen"><QrCode size={15} className="mr-1" /> Einstellungen &amp; QR</TabsTrigger>
          <TabsTrigger value="beschreibung" data-testid="tab-beschreibung"><FileText size={15} className="mr-1" /> Kursbeschreibung</TabsTrigger>
          <TabsTrigger value="formular-g" data-testid="tab-formular-gemeinsam"><ClipboardList size={15} className="mr-1" /> Anmeldeformular</TabsTrigger>
          <TabsTrigger value="anmeldungen" data-testid="tab-anmeldungen"><Users size={15} className="mr-1" /> Anmeldungen</TabsTrigger>
          <TabsTrigger value="termine" data-testid="tab-termine"><CalendarDays size={15} className="mr-1" /> Terminkalender</TabsTrigger>
          <TabsTrigger value="dateien" data-testid="tab-dateien"><FolderOpen size={15} className="mr-1" /> Dateiablage</TabsTrigger>
          <TabsTrigger value="umfrage" data-testid="tab-umfrage"><Vote size={15} className="mr-1" /> Terminumfrage</TabsTrigger>
          <TabsTrigger value="statistik" data-testid="tab-statistik"><BarChart3 size={15} className="mr-1" /> Statistik</TabsTrigger>
        </TabsList>

        {/* Einstellungen & QR */}
        <TabsContent value="einstellungen" className="pt-6">
          <div className="bg-white border border-border rounded-lg shadow-sm p-6 mb-6">
            <h3 className="font-semibold mb-4">Kurs bearbeiten</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Kurs-Name</Label><Input value={edit.kurs_name} onChange={(e) => setEdit({ ...edit, kurs_name: e.target.value })} data-testid="edit-course-name" /></div>
              <div className="space-y-2"><Label>Kurs-Gemeinde</Label><Input value={edit.kurs_gemeinde} onChange={(e) => setEdit({ ...edit, kurs_gemeinde: e.target.value })} data-testid="edit-course-gemeinde" /></div>
              <div className="space-y-2">
                <Label>Sakrament</Label>
                <Select value={edit.sakrament} onValueChange={(v) => setEdit({ ...edit, sakrament: v })}>
                  <SelectTrigger data-testid="edit-course-sakrament"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TA">TA – Taufe</SelectItem>
                    <SelectItem value="EK">EK – Erstkommunion</SelectItem>
                    <SelectItem value="FI">FI – Firmung</SelectItem>
                    <SelectItem value="EH">EH – Eheschließung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {["TA", "EH"].includes(edit.sakrament) && (
                <div className="space-y-2">
                  <Label>Monat (01–12)</Label>
                  <Select value={edit.monat} onValueChange={(v) => setEdit({ ...edit, monat: v })}>
                    <SelectTrigger data-testid="edit-course-monat"><SelectValue placeholder="Monat wählen" /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2"><Label>Jahr</Label><Input type="number" value={edit.jahr} onChange={(e) => setEdit({ ...edit, jahr: e.target.value })} data-testid="edit-course-jahr" /></div>
              <div className="space-y-2"><Label>Ort</Label><Input value={edit.ort} onChange={(e) => setEdit({ ...edit, ort: e.target.value })} data-testid="edit-course-ort" /></div>
              <div className="space-y-2"><Label>Online bis</Label><Input type="date" value={edit.online_until} onChange={(e) => setEdit({ ...edit, online_until: e.target.value })} data-testid="edit-course-online-until" /></div>
              <div className="space-y-2"><Label>Kurs-Beginn</Label><Input type="date" value={edit.kurs_beginn} onChange={(e) => setEdit({ ...edit, kurs_beginn: e.target.value })} data-testid="edit-course-beginn" /></div>
              <div className="space-y-2"><Label>Kurs-Ende</Label><Input type="date" value={edit.kurs_ende} onChange={(e) => setEdit({ ...edit, kurs_ende: e.target.value })} data-testid="edit-course-ende" /></div>
            </div>
            <div className="flex items-center justify-between mt-5">
              <div className="flex gap-2">
                <Button onClick={saveCourse} data-testid="save-course-settings-button"><Save size={16} className="mr-1" /> Änderungen speichern</Button>
                <Button variant="outline" onClick={() => { setDup({ kurs_name: "", jahr: edit.jahr, monat: edit.monat || "", ort: edit.ort || "", kurs_gemeinde: edit.kurs_gemeinde || "" }); setDupOpen(true); }} data-testid="duplicate-course-button"><CopyPlus size={16} className="mr-1" /> Kurs duplizieren</Button>
              </div>
              <Button variant="destructive" onClick={deleteCourse} data-testid="delete-course-button"><Trash2 size={16} className="mr-1" /> Kurs löschen</Button>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Power size={18} /> Verfügbarkeit</h3>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div>
                  <p className="text-sm font-medium">Kurs online</p>
                  <p className="text-xs text-muted-foreground">Steuert, ob eine Anmeldung möglich ist.</p>
                </div>
                <Switch checked={course.online} onCheckedChange={toggleOnline} data-testid="online-toggle" />
              </div>
              <div className="py-3 text-sm space-y-1">
                <p><span className="text-muted-foreground">Beginn:</span> {course.kurs_beginn || "—"}</p>
                <p><span className="text-muted-foreground">Ende:</span> {course.kurs_ende || "—"}</p>
                <p><span className="text-muted-foreground">Online bis:</span> {course.online_until || "—"}</p>
              </div>
            </div>

            <div className="bg-white border border-border rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><QrCode size={18} /> QR-Code &amp; Link</h3>
              {qr ? (
                <div className="flex flex-col items-center">
                  <img src={qr.qr} alt="QR-Code" className="w-44 h-44" data-testid="qr-image" />
                  <div className="flex w-full gap-2 mt-4">
                    <Input readOnly value={qr.link} className="text-xs" data-testid="qr-link" />
                    <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link-button"><Copy size={15} /></Button>
                  </div>
                  <a href={qr.qr} download={`qr-${course.kurs_name}.png`} className="mt-3 text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="download-qr-button">
                    <Download size={15} /> QR-Code herunterladen
                  </a>
                </div>
              ) : <p className="text-sm text-muted-foreground">Wird geladen…</p>}
            </div>
          </div>
        </TabsContent>

        {/* Kursbeschreibung */}
        <TabsContent value="beschreibung" className="pt-6">
          <div className="bg-white border border-border rounded-lg shadow-sm p-6 max-w-3xl">
            <Label className="mb-2 block">Kursbeschreibung</Label>
            <RichTextEditor value={beschreibung} onChange={setBeschreibung} placeholder="Beschreiben Sie den Kurs…" testid="beschreibung-editor" />
            <Button className="mt-4" onClick={saveBeschreibung} data-testid="save-course-button"><Save size={16} className="mr-1" /> Speichern</Button>
          </div>
        </TabsContent>

        {/* Anmeldeformular */}
        <TabsContent value="formular-g" className="pt-6">
          <p className="text-sm text-muted-foreground mb-4 max-w-2xl">Anmeldeformular für die Eltern. Beim Absenden wird automatisch ein PDF erzeugt. Über <strong>Sichtbarkeitsbedingungen</strong> lassen sich Felder für getrennte Anmeldungen ein- oder ausblenden.</p>
          <div className="flex flex-wrap items-center gap-2 mb-5 p-3 bg-slate-50 border border-border rounded-lg">
            <Button variant="outline" size="sm" onClick={copyForm} data-testid="copy-form-button"><CopyPlus size={15} className="mr-1" /> Formular kopieren</Button>
            <Button variant="outline" size="sm" onClick={pasteForm} data-testid="paste-form-button"><ClipboardPaste size={15} className="mr-1" /> Einfügen</Button>
            <Button variant="outline" size="sm" onClick={() => { if (!fields.length) return toast.error("Noch keine Felder angelegt."); setPreviewFields(fields); setPreviewOpen(true); }} data-testid="preview-form-button"><Eye size={15} className="mr-1" /> Vorschau</Button>
            <Button variant="outline" size="sm" onClick={() => { setTplSak(course.sakrament); setTplName(course.kurs_name || ""); setSaveTplOpen(true); }} data-testid="save-as-template-button"><Save size={15} className="mr-1" /> Als Vorlage speichern</Button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-muted-foreground">Aus Vorlage:</span>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="w-48" data-testid="apply-template-select"><SelectValue placeholder="Vorlage wählen" /></SelectTrigger>
                <SelectContent>
                  {formTemplates.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Keine Vorlagen vorhanden.</div>}
                  {formTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{(SAKRAMENT_LABELS[t.sakrament] || t.sakrament)} – {t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">Aus Kurs übernehmen:</span>
              <Select onValueChange={importFromCourse}>
                <SelectTrigger className="w-56" data-testid="import-form-select"><SelectValue placeholder="Kurs wählen" /></SelectTrigger>
                <SelectContent>
                  {allCourses.filter((c) => c.id !== cid).map((c) => <SelectItem key={c.id} value={c.id}>{c.kurs_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <FormBuilder key={"g" + fields.map((f) => f.id).join()} fields={fields} onChange={setFields} dsTemplates={dsTemplates} sakrament={course.sakrament} />
          <Button className="mt-6" onClick={saveForm} data-testid="save-form-button"><Save size={16} className="mr-1" /> Formular speichern</Button>
        </TabsContent>

        {/* Anmeldungen */}
        <TabsContent value="anmeldungen" className="pt-6">
          <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Kind</TableHead><TableHead>Eltern (E-Mail)</TableHead><TableHead>Eingegangen</TableHead><TableHead className="text-right">PDF</TableHead></TableRow></TableHeader>
              <TableBody>
                {regs.map((r) => (
                  <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                    <TableCell>{r.kind_name || "—"}</TableCell>
                    <TableCell className="text-sm">{r.user_email}</TableCell>
                    <TableCell className="text-xs">{new Date(r.submitted_at).toLocaleString("de-DE")}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-3">
                        {canMove && (
                          <button onClick={() => { setMoveTarget(""); setMoveReg(r); }} className="text-slate-600 inline-flex items-center gap-1 text-sm hover:text-primary" data-testid={`move-reg-${r.id}`}>
                            <ArrowRightLeft size={15} /> Verschieben
                          </button>
                        )}
                        <a href={`${API}/registrations/${r.id}/pdf?token=${getToken()}`} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 text-sm hover:underline" data-testid={`reg-pdf-${r.id}`}>
                          <Download size={15} /> PDF
                        </a>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {regs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Noch keine Anmeldungen.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Terminkalender */}
        <TabsContent value="termine" className="pt-6">
          <TermineManager cid={cid} termine={termine} reload={reloadAll} />
        </TabsContent>

        {/* Dateiablage */}
        <TabsContent value="dateien" className="pt-6">
          <FilesManager cid={cid} files={files} regs={regs} reload={reloadAll} />
        </TabsContent>

        {/* Terminumfrage */}
        <TabsContent value="umfrage" className="pt-6">
          <PollsManager cid={cid} polls={polls} reload={reloadAll} />
        </TabsContent>

        {/* Statistik */}
        <TabsContent value="statistik" className="pt-6">
          {!stats ? <p className="text-muted-foreground text-sm">Wird geladen…</p> : (
            <div className="space-y-6" data-testid="stats-panel">
              <div className="grid sm:grid-cols-4 gap-4">
                <div className="bg-white border border-border rounded-lg p-5 shadow-sm"><Users className="text-primary mb-2" size={20} /><p className="text-2xl font-serif" data-testid="stat-registrations">{stats.registrations_count}</p><p className="text-sm text-muted-foreground">Anmeldungen</p></div>
                <div className="bg-white border border-border rounded-lg p-5 shadow-sm"><Users className="text-primary mb-2" size={20} /><p className="text-2xl font-serif" data-testid="stat-enrolled">{stats.enrolled_users}</p><p className="text-sm text-muted-foreground">Registrierte Eltern</p></div>
                <div className="bg-white border border-border rounded-lg p-5 shadow-sm"><CalendarDays className="text-primary mb-2" size={20} /><p className="text-2xl font-serif">{stats.termine_count}</p><p className="text-sm text-muted-foreground">Termine</p></div>
                <div className="bg-white border border-border rounded-lg p-5 shadow-sm"><FolderOpen className="text-primary mb-2" size={20} /><p className="text-2xl font-serif">{stats.files_count}</p><p className="text-sm text-muted-foreground">Dateien</p></div>
              </div>
              <div className="bg-white border border-border rounded-lg shadow-sm p-5">
                <h3 className="font-semibold mb-3">Rückmeldungen zu Terminumfragen</h3>
                {stats.polls.length === 0 ? <p className="text-sm text-muted-foreground">Keine Umfragen angelegt.</p> : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-left"><th className="py-2">Umfrage</th><th>Frist</th><th>Rückgemeldet</th><th>Offen</th><th>Status</th></tr></thead>
                    <tbody>
                      {stats.polls.map((p) => (
                        <tr key={p.id} className="border-b border-border" data-testid={`stat-poll-${p.id}`}>
                          <td className="py-2">{p.titel}</td>
                          <td>{p.frist ? new Date(p.frist).toLocaleDateString("de-DE") : "—"}</td>
                          <td>{p.responded}</td>
                          <td className={p.open > 0 ? "text-amber-600 font-medium" : ""}>{p.open}</td>
                          <td>{p.closed ? <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">geschlossen</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">offen</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!moveReg} onOpenChange={(o) => !o && setMoveReg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Anmeldung verschieben</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Anmeldung „{moveReg?.kind_name || moveReg?.user_email}" in einen anderen Kurs verschieben. Die Eltern erhalten automatisch Zugriff auf den Zielkurs.</p>
          <div className="space-y-2 py-2">
            <Label>Zielkurs</Label>
            <Select value={moveTarget} onValueChange={setMoveTarget}>
              <SelectTrigger data-testid="move-target-select"><SelectValue placeholder="Kurs wählen" /></SelectTrigger>
              <SelectContent>
                {allCourses.filter((c) => c.id !== cid).map((c) => <SelectItem key={c.id} value={c.id}>{c.kurs_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={doMove} data-testid="confirm-move-button">Verschieben</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kurs duplizieren</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Alle Inhalte (Anmeldeformular, Beschreibung, Termine, Terminumfragen, Dateiablage) werden kopiert. Anmeldungen werden nicht übernommen. Bitte vergeben Sie einen neuen Kurs-Namen. Ein neuer QR-Code wird automatisch erzeugt.</p>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>Neuer Kurs-Name</Label><Input value={dup.kurs_name} onChange={(e) => setDup({ ...dup, kurs_name: e.target.value })} placeholder={`${course.kurs_name} (Kopie)`} data-testid="dup-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Jahr</Label><Input type="number" value={dup.jahr} onChange={(e) => setDup({ ...dup, jahr: e.target.value })} data-testid="dup-jahr" /></div>
              {["TA", "EH"].includes(edit.sakrament) && (
                <div className="space-y-2">
                  <Label>Monat</Label>
                  <Select value={dup.monat} onValueChange={(v) => setDup({ ...dup, monat: v })}>
                    <SelectTrigger data-testid="dup-monat"><SelectValue placeholder="Monat" /></SelectTrigger>
                    <SelectContent>{Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2"><Label>Ort</Label><Input value={dup.ort} onChange={(e) => setDup({ ...dup, ort: e.target.value })} data-testid="dup-ort" /></div>
            <div className="space-y-2"><Label>Kurs-Gemeinde</Label><Input value={dup.kurs_gemeinde} onChange={(e) => setDup({ ...dup, kurs_gemeinde: e.target.value })} data-testid="dup-gemeinde" /></div>
          </div>
          <DialogFooter><Button onClick={doDuplicate} data-testid="confirm-duplicate-button"><CopyPlus size={16} className="mr-1" /> Duplizieren</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader><DialogTitle>Als Vorlage speichern</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Das aktuelle Formular wird unter „Verwaltung › Anmeldeformulare" als wiederverwendbare Vorlage gespeichert.</p>
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            <div className="space-y-2">
              <Label>Sakrament</Label>
              <Select value={tplSak} onValueChange={setTplSak}>
                <SelectTrigger data-testid="save-template-sakrament"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TA">Taufe</SelectItem>
                  <SelectItem value="EK">Erstkommunion</SelectItem>
                  <SelectItem value="FI">Firmung</SelectItem>
                  <SelectItem value="EH">Eheschließung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name der Vorlage</Label>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="z.B. Firmung Standard" data-testid="save-template-name" />
            </div>
          </div>
          <DialogFooter><Button onClick={saveAsTemplate} data-testid="confirm-save-template-button"><Save size={16} className="mr-1" /> Vorlage speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="form-preview-dialog">
          <DialogHeader><DialogTitle>Vorschau: Anmeldeformular</DialogTitle></DialogHeader>
          <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
            <Checkbox checked={previewSkip} onCheckedChange={(v) => setPreviewSkip(!!v)} data-testid="preview-skip-required" />
            <span>Pflichtfelder zum Testen ignorieren (Weiterblättern & Absenden ohne Ausfüllen)</span>
          </label>
          <div className="pt-2">
            <FormRenderer key={previewSkip ? "skip" : "req"} courseId={cid} fields={previewFields} submitting={false} skipRequired={previewSkip} dsTemplates={dsTemplates} onSubmit={(_p, reset) => { toast.info("Vorschau – es wurde keine echte Anmeldung gespeichert."); reset && reset(); }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TermineManager({ cid, termine, reload }) {
  const [form, setForm] = useState({ titel: "", datum: "", uhrzeit: "", beschreibung: "" });
  const add = async () => {
    if (!form.titel || !form.datum) return toast.error("Titel und Datum erforderlich.");
    await api.post(`/courses/${cid}/termine`, form);
    setForm({ titel: "", datum: "", uhrzeit: "", beschreibung: "" });
    toast.success("Termin angelegt."); reload();
  };
  const del = async (id) => { await api.delete(`/termine/${id}`); reload(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="bg-white border border-border rounded-lg shadow-sm p-5 space-y-3">
        <h3 className="font-semibold">Neuer Termin</h3>
        <Input placeholder="Titel" value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} data-testid="termin-titel" />
        <Input type="date" value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} data-testid="termin-datum" />
        <Input type="time" value={form.uhrzeit} onChange={(e) => setForm({ ...form, uhrzeit: e.target.value })} data-testid="termin-uhrzeit" />
        <RichTextEditor value={form.beschreibung} onChange={(v) => setForm({ ...form, beschreibung: v })} placeholder="Beschreibung" testid="termin-beschreibung-editor" />
        <Button onClick={add} className="w-full" data-testid="add-termin-button"><Plus size={16} className="mr-1" /> Termin anlegen</Button>
      </div>
      <div className="lg:col-span-2 space-y-3">
        {termine.map((t) => (
          <div key={t.id} className="bg-white border border-border rounded-lg shadow-sm p-4 flex items-start justify-between" data-testid={`termin-${t.id}`}>
            <div>
              <p className="font-medium">{t.titel}</p>
              <p className="text-sm text-muted-foreground">{new Date(t.datum).toLocaleDateString("de-DE")}{t.uhrzeit && ` · ${t.uhrzeit}`}</p>
              {t.beschreibung && <RichContent html={t.beschreibung} className="text-sm mt-1" />}
            </div>
            <Button variant="ghost" size="sm" onClick={() => del(t.id)}><Trash2 size={16} className="text-destructive" /></Button>
          </div>
        ))}
        {termine.length === 0 && <p className="text-muted-foreground text-sm">Noch keine Termine.</p>}
      </div>
    </div>
  );
}

function FilesManager({ cid, files, regs, reload }) {
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState("all");
  const users = Array.from(new Map(regs.map((r) => [r.user_id, r.user_email])).entries());
  const upload = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (target !== "all") fd.append("target_user_id", target);
    try {
      await api.post(`/courses/${cid}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Datei hochgeladen."); setFile(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/files/${id}`); reload(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="bg-white border border-border rounded-lg shadow-sm p-5 space-y-3">
        <h3 className="font-semibold">Datei hochladen</h3>
        <Input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="file-input" />
        <Label className="text-xs">Sichtbar für</Label>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="file-target"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Ganzer Kurs</SelectItem>
            {users.map(([id, email]) => <SelectItem key={id} value={id}>{email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={upload} className="w-full" data-testid="upload-file-button"><Plus size={16} className="mr-1" /> Hochladen</Button>
      </div>
      <div className="lg:col-span-2 space-y-2">
        {files.map((f) => (
          <div key={f.id} className="bg-white border border-border rounded-lg shadow-sm p-4 flex items-center justify-between" data-testid={`file-${f.id}`}>
            <div className="min-w-0">
              <p className="font-medium truncate">{f.original_filename}</p>
              <p className="text-xs text-muted-foreground">{f.target_user_id ? "Einzelperson" : "Ganzer Kurs"}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <a href={`${API}/files/${f.id}/download?token=${getToken()}`} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><Download size={16} /></Button></a>
              <Button variant="ghost" size="sm" onClick={() => del(f.id)}><Trash2 size={16} className="text-destructive" /></Button>
            </div>
          </div>
        ))}
        {files.length === 0 && <p className="text-muted-foreground text-sm">Noch keine Dateien.</p>}
      </div>
    </div>
  );
}

function PollsManager({ cid, polls, reload }) {
  const rid = () => Math.random().toString(36).slice(2, 8);
  const [titel, setTitel] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [frist, setFrist] = useState("");
  const [options, setOptions] = useState([{ id: rid(), datum: "", uhrzeit: "" }]);
  const [dragIdx, setDragIdx] = useState(null);
  const create = async () => {
    if (!titel || options.every((o) => !o.datum)) return toast.error("Titel und mind. ein Datum erforderlich.");
    await api.post(`/courses/${cid}/polls`, { titel, beschreibung, frist: frist || null, optionen: options.filter((o) => o.datum).map(({ datum, uhrzeit }) => ({ datum, uhrzeit })) });
    setTitel(""); setBeschreibung(""); setFrist(""); setOptions([{ id: rid(), datum: "", uhrzeit: "" }]); toast.success("Umfrage erstellt."); reload();
  };
  const moveOption = (i, j) => {
    if (j < 0 || j >= options.length) return;
    const n = [...options];
    [n[i], n[j]] = [n[j], n[i]];
    setOptions(n);
  };
  const dropOn = (i) => {
    if (dragIdx === null || dragIdx === i) return setDragIdx(null);
    const n = [...options];
    const [m] = n.splice(dragIdx, 1);
    n.splice(i, 0, m);
    setOptions(n);
    setDragIdx(null);
  };
  const del = async (id) => { await api.delete(`/polls/${id}`); reload(); };
  return (
    <div className="space-y-6">
      <div className="bg-white border border-border rounded-lg shadow-sm p-5 max-w-2xl space-y-3">
        <h3 className="font-semibold">Neue Terminumfrage</h3>
        <Input placeholder="Titel der Umfrage" value={titel} onChange={(e) => setTitel(e.target.value)} data-testid="poll-titel" />
        <RichTextEditor value={beschreibung} onChange={setBeschreibung} placeholder="Beschreibung (optional)" testid="poll-beschreibung-editor" />
        {options.map((o, i) => (
          <div
            key={o.id}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => dropOn(i)}
            className={`flex gap-2 items-center rounded-md ${dragIdx === i ? "opacity-50" : ""}`}
            data-testid={`poll-option-row-${i}`}
          >
            <span className="cursor-grab active:cursor-grabbing text-slate-400 shrink-0" title="Zum Sortieren ziehen" data-testid={`poll-option-drag-${i}`}><GripVertical size={16} /></span>
            <Input type="date" value={o.datum} onChange={(e) => { const n = [...options]; n[i].datum = e.target.value; setOptions(n); }} data-testid={`poll-option-datum-${i}`} />
            <Input type="time" value={o.uhrzeit} onChange={(e) => { const n = [...options]; n[i].uhrzeit = e.target.value; setOptions(n); }} data-testid={`poll-option-uhrzeit-${i}`} />
            <Button variant="ghost" size="sm" onClick={() => moveOption(i, i - 1)} disabled={i === 0} data-testid={`poll-option-up-${i}`}><ChevronUp size={14} /></Button>
            <Button variant="ghost" size="sm" onClick={() => moveOption(i, i + 1)} disabled={i === options.length - 1} data-testid={`poll-option-down-${i}`}><ChevronDown size={14} /></Button>
            {options.length > 1 && <Button variant="ghost" size="sm" onClick={() => setOptions(options.filter((_, x) => x !== i))}><Trash2 size={14} /></Button>}
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setOptions([...options, { id: rid(), datum: "", uhrzeit: "" }])}><Plus size={14} className="mr-1" /> Terminvorschlag</Button>
        <div className="space-y-1.5 pt-1">
          <Label className="text-xs">Rückmelde-Frist (optional) – danach wird die Umfrage automatisch geschlossen</Label>
          <Input type="date" value={frist} onChange={(e) => setFrist(e.target.value)} data-testid="poll-frist" />
        </div>
        <Button onClick={create} className="w-full" data-testid="create-poll-button">Umfrage erstellen</Button>
      </div>

      {polls.map((p) => (
        <div key={p.id} className="bg-white border border-border rounded-lg shadow-sm p-5 overflow-x-auto" data-testid={`poll-${p.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold">{p.titel}</h3>
              {p.beschreibung && <RichContent html={p.beschreibung} className="text-sm mt-1" />}
              {p.frist && <p className="text-xs text-muted-foreground">Frist: {new Date(p.frist).toLocaleDateString("de-DE")} · {p.closed ? "geschlossen" : "offen"}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={() => del(p.id)}><Trash2 size={16} className="text-destructive" /></Button>
          </div>
          <table className="text-sm w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4">Teilnehmer:in</th>
                {p.optionen.map((o) => <th key={o.id} className="px-3 py-2 whitespace-nowrap">{new Date(o.datum).toLocaleDateString("de-DE")}<br /><span className="font-normal text-xs text-muted-foreground">{o.uhrzeit}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {p.responses.map((r) => (
                <tr key={r.id} className="border-b border-border">
                  <td className="py-2 pr-4">{r.name || r.user_email}</td>
                  {p.optionen.map((o) => (
                    <td key={o.id} className="text-center">{r.antworten[o.id] === "ja" ? <span className="text-green-600">✓</span> : r.antworten[o.id] === "nein" ? <span className="text-destructive">✗</span> : "–"}</td>
                  ))}
                </tr>
              ))}
              {p.responses.length === 0 && <tr><td colSpan={p.optionen.length + 1} className="text-muted-foreground py-4 text-center">Noch keine Rückmeldungen.</td></tr>}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
